import React, { useMemo } from 'react';
import { SxProps, Box, Typography } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useDateService } from '../../../utils/hooks/useDateService';
import useFieldHelper from '../fieldHelper';
import { unitOfTime } from 'moment/moment';

interface DurationBetweenTwoDatesIconFieldProps {
  dateFromSource?: Nullable<string>;
  dateToSource?: Nullable<string>;
  dateFromValue?: moment.MomentInput;
  dateToValue?: moment.MomentInput;

  iconSx?: SxProps;
  textSx?: SxProps;
  className?: string;
  style?: React.CSSProperties;
  textColor?: string | ((startDate: string, endDate: string) => string);
  prefix?: string;

  granularity?: unitOfTime.StartOf;
}

const DurationBetweenTwoDatesIconField: React.FC<DurationBetweenTwoDatesIconFieldProps> = ({
  dateFromSource,
  dateFromValue,
  dateToSource,
  dateToValue,
  iconSx,
  textSx,
  className,
  style,
  textColor,
  prefix,
  granularity = 'day',
}) => {
  const { getValue: getDateFromValue } = useFieldHelper({ source: dateFromSource, value: dateFromValue });
  const { getValue: getDateToValue } = useFieldHelper({ source: dateToSource, value: dateToValue });
  const { getToday, getMomentDateObj, getStringDurationBetweenTwoDates } = useDateService();

  const startDate = getDateFromValue() as string;
  const endDate = (!dateToSource && !dateToValue ? getToday() : getDateToValue()) as string;

  const duractionString = useMemo(() => {
    if (!startDate) {
      return '0d';
    }

    const lastCheckDate = getMomentDateObj(startDate);
    if (lastCheckDate.isSame(endDate, granularity)) {
      return '0d';
    }

    const durationString = getStringDurationBetweenTwoDates(lastCheckDate, endDate);
    return durationString
      ? durationString
          .replace(/ years?/g, 'y')
          .replace(/ months?/g, 'm')
          .replace(/ days?/g, 'd')
          .replace(/,\s/g, ' ')
          .trim()
      : '0d';
  }, [startDate, getMomentDateObj, endDate, granularity, getStringDurationBetweenTwoDates]);

  const resolvedColor = typeof textColor === 'function' ? textColor(startDate, endDate) : textColor;

  return (
    <Box display="flex" alignItems="center" gap={1} color={resolvedColor} className={className} style={style}>
      <AccessTimeIcon
        sx={
          iconSx || {
            fontSize: '12px',
          }
        }
      />
      <Typography variant="body2" sx={textSx}>
        {prefix}
        {duractionString}
      </Typography>
    </Box>
  );
};

export default DurationBetweenTwoDatesIconField;
